import { useEffect, useId, useRef, useState } from "react";
import { normaliseMethodId, registerGate, registerUsers } from "../lib/eamRegister";
import type { RegisterRow, RegisterRun } from "../lib/eamRegister";
import { registerCopy } from "../lib/eam";
import { graphCaller, signInProblem, signInToRegister } from "../lib/entraSignIn";
import type { EamEntra, EamRealm, EamUser } from "../types/api";

const BUTTON =
  "inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-subtle text-sm font-medium text-white bg-accent hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
const LINK = "text-sm font-medium text-accent hover:text-accent-dark disabled:opacity-50";

interface Props {
  realm: EamRealm;
  entra: EamEntra;
  users: readonly EamUser[] | null;
  busy: boolean;
  /** ok is true only when saved; a field error is shown under the field, a page error by the page. */
  onSaveMethodId: (id: string) => Promise<{ ok: boolean; fieldError: string | null }>;
}

function rowText(row: RegisterRow): string {
  return row.outcome === "failed" ? registerCopy.failed(row.detail) : registerCopy.results[row.outcome];
}

function stopText(run: RegisterRun): string | null {
  if (run.stop === "no_role") return registerCopy.stops.no_role;
  if (run.stop === "bad_method_id") return registerCopy.stops.bad_method_id(run.stopDetail);
  return null;
}

export default function EamRegisterPanel({ realm, entra, users, busy, onSaveMethodId }: Props) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [rows, setRows] = useState<RegisterRow[]>([]);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState<RegisterRun | null>(null);
  const [signInError, setSignInError] = useState<string | null>(null);
  const errorId = useId();
  const runningRef = useRef(false);

  // A new user list makes the old rows stale; a run in progress keeps its own.
  useEffect(() => {
    if (runningRef.current) return;
    setRows([]);
    setDone(null);
    setSignInError(null);
  }, [users]);

  const gate = registerGate(realm, entra, users);
  if (gate === "none") return null;
  if (gate === "moving") return <p className="mt-3 text-sm text-gray-700">{registerCopy.finishMoveFirst}</p>;

  const save = async () => {
    const id = normaliseMethodId(value);
    if (!id) {
      setProblem(registerCopy.methodIdInvalid);
      return;
    }
    const result = await onSaveMethodId(id);
    setProblem(result.fieldError);
    if (result.ok) {
      setEditing(false);
      setValue("");
    }
  };

  const cancel = () => {
    setEditing(false);
    setValue("");
    setProblem(null);
  };

  const field = (
    <form
      className="mt-3"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <label htmlFor="methodId" className="block text-sm font-medium text-gray-700">
        {registerCopy.methodIdLabel}
      </label>
      <p className="mt-1 text-xs text-gray-500">{registerCopy.methodIdHint}</p>
      <div className="mt-1 flex flex-wrap items-start gap-3">
        <input
          id="methodId"
          className="input min-w-0 flex-1 font-mono"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setProblem(null);
          }}
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? errorId : undefined}
          autoComplete="off"
          spellCheck={false}
        />
        <button type="submit" className={BUTTON} disabled={busy || running}>
          {registerCopy.save}
        </button>
        {editing && (
          <button type="button" className={LINK} onClick={cancel}>
            {registerCopy.cancel}
          </button>
        )}
      </div>
      {problem && (
        <p id={errorId} className="mt-1 text-xs text-red-600">
          {problem}
        </p>
      )}
    </form>
  );

  if (gate === "method_id") return field;

  // The gate is 'ready': these hold, and narrow the types.
  const methodId = entra.method_id;
  const clientId = realm.sign_in_app?.app_id;
  if (!methodId || !clientId || !users) return null;

  const register = async () => {
    runningRef.current = true;
    setRunning(true);
    setRows([]);
    setDone(null);
    setSignInError(null);
    try {
      // The token stays in this scope: not in state, storage or a log.
      let token: string;
      try {
        token = await signInToRegister(realm.tenant_id, clientId);
      } catch (e) {
        const kind = signInProblem(e);
        setSignInError(
          kind === "other"
            ? registerCopy.signIn.other(e instanceof Error ? e.message : String(e))
            : registerCopy.signIn[kind],
        );
        return;
      }
      try {
        setDone(
          await registerUsers(users, methodId, graphCaller(token), {
            onRow: (row) => setRows((r) => [...r, row]),
          }),
        );
      } catch (e) {
        // Rows already shown stay; some users may be registered.
        setSignInError(registerCopy.runStopped(e instanceof Error ? e.message : String(e)));
      }
    } finally {
      runningRef.current = false;
      setRunning(false);
    }
  };

  const shown = done?.rows ?? rows;
  const count = (o: RegisterRow["outcome"]) => shown.filter((r) => r.outcome === o).length;
  const stop = done ? stopText(done) : null;

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="break-all font-mono text-gray-800">{methodId}</span>
        {!editing && (
          <button className={LINK} disabled={running} onClick={() => setEditing(true)}>
            {registerCopy.change}
          </button>
        )}
      </div>
      {editing && field}
      <p className="mt-3 text-sm text-gray-500">{registerCopy.consent}</p>
      <p className="mt-1 text-sm text-gray-500">{registerCopy.token}</p>
      <button className={`${BUTTON} mt-3`} disabled={running || busy} onClick={() => void register()}>
        {running ? registerCopy.progress(rows.length, users.length) : registerCopy.button}
      </button>

      {signInError && (
        <div className="mt-3 rounded-md bg-red-50 p-3" role="alert">
          <p className="text-sm text-red-700">{signInError}</p>
        </div>
      )}
      {stop && (
        <div className="mt-3 rounded-md bg-amber-50 p-3" role="alert">
          <p className="text-sm text-amber-800">{stop}</p>
        </div>
      )}

      {shown.length > 0 && (
        <div className="mt-4">
          {done && (
            <p className="text-sm text-gray-700">
              {registerCopy.summary(
                count("registered_now"),
                count("registered_already"),
                shown.length - count("registered_now") - count("registered_already"),
              )}
            </p>
          )}
          <div className="mt-2 divide-y divide-gray-100 text-sm">
            <div className="flex gap-3 pb-2 text-xs uppercase tracking-wide text-gray-400">
              <span className="w-40 flex-shrink-0">{registerCopy.headings.phone}</span>
              <span className="w-80 flex-shrink-0">{registerCopy.headings.objectId}</span>
              <span>{registerCopy.headings.result}</span>
            </div>
            {shown.map((row) => (
              <div key={row.user.id} className="flex flex-wrap items-center gap-3 py-2">
                <span className="w-40 flex-shrink-0 font-mono text-gray-800">{row.user.phone ?? registerCopy.noNumber}</span>
                <span className="w-80 flex-shrink-0 break-all font-mono text-gray-800">{row.user.object_id}</span>
                <span className="min-w-0 flex-1 text-gray-700">{rowText(row)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {done && (
        <p className="mt-4 text-sm text-gray-500">
          <a href={registerCopy.removeUrl} target="_blank" rel="noopener noreferrer" className={LINK}>
            {registerCopy.removeLink}
          </a>{" "}
          {registerCopy.removePath}
        </p>
      )}
    </div>
  );
}
